/**
 * Conversation service for aggregating messages into conversations.
 *
 * Handles business logic for grouping messages, calculating unread counts,
 * and sorting conversations. Keeps aggregation logic separate from pure
 * data transformation functions.
 *
 * @module conversationService
 */

import { Conversation } from '@/types/partner-finder';
import type { MessageAPI, SessionCount } from '@/types/api/partner';
import { formatTime } from '@/lib/mappers';
import { DEFAULT_PARTNER_LEVEL } from '@/lib/constants';

const SESSION_MESSAGE_LIMIT = 10;

/**
 * Aggregates API messages into Conversation domain objects.
 * Groups messages by conversation partner, calculates unread counts,
 * and sorts by most recent update time.
 *
 * @param messages - Array of API messages
 * @param currentUserId - Current user ID
 * @param activePartnerIds - Set of user IDs that currently have an active partner request.
 *   Pass `null` when the status is unknown (e.g., fetch failed) — messaging stays enabled.
 * @param sessionCounts - Per-session sent-message counts from the backend (source of truth).
 * @returns Array of Conversation objects sorted by update time
 */
export function aggregateMessagesToConversations(
  messages: MessageAPI[],
  currentUserId: string,
  activePartnerIds: Set<string> | null = null,
  sessionCounts: SessionCount[] = [],
): Conversation[] {
  const conversationMap = new Map<string, Conversation>();

  // Messages arrive sorted DESC (newest first) from the API.
  // By repeatedly overwriting this map, the final value per conversation
  // will be the oldest message's sender — i.e. the conversation initiator.
  const initiatorMap = new Map<string, string>();

  messages.forEach((msg) => {
    const otherUserId = msg.fromUserId === currentUserId ? msg.toUserId : msg.fromUserId;
    const otherUserName =
      msg.fromUserId === currentUserId ? msg.toUserName || 'Unknown' : msg.fromUserName;
    const conversationId = `conv-${otherUserId}`;

    initiatorMap.set(conversationId, msg.fromUserId);

    const existingConv = conversationMap.get(conversationId);
    if (existingConv) {
      if (new Date(msg.createdAt) > new Date(existingConv.updatedAt)) {
        existingConv.lastMessage = msg.content;
        existingConv.lastMessageTime = formatTime(msg.createdAt);
        existingConv.updatedAt = msg.createdAt;
      }
      if (msg.fromUserId !== currentUserId && !msg.isRead) {
        existingConv.unreadCount++;
      }
    } else {
      conversationMap.set(conversationId, {
        id: conversationId,
        partnerId: otherUserId,
        partnerName: otherUserName,
        partnerLevel: DEFAULT_PARTNER_LEVEL, // API doesn't provide level in messages
        lastMessage: msg.content,
        lastMessageTime: formatTime(msg.createdAt),
        unreadCount: msg.fromUserId !== currentUserId && !msg.isRead ? 1 : 0,
        updatedAt: msg.createdAt,
        partnerHasActiveRequest: true, // resolved below once all messages are processed
      });
    }
  });

  // Build a lookup map from partnerId → sessionCount for O(1) access
  const countByPartnerId = new Map<string, SessionCount>();
  sessionCounts.forEach((sc) => countByPartnerId.set(sc.partnerId, sc));

  conversationMap.forEach((conv, convId) => {
    // Resolve partnerHasActiveRequest — only the initiator needs the partner's request active
    const initiatorId = initiatorMap.get(convId);
    const currentUserIsInitiator = initiatorId === currentUserId;
    conv.partnerHasActiveRequest = currentUserIsInitiator
      ? activePartnerIds === null || activePartnerIds.has(conv.partnerId)
      : true;

    // Attach sessionInfo from backend counts — every conversation is a session
    const sc = countByPartnerId.get(conv.partnerId);
    const sent = sc?.messagesSent ?? 0;
    conv.sessionInfo = { sent, remaining: Math.max(0, SESSION_MESSAGE_LIMIT - sent) };
  });

  return Array.from(conversationMap.values()).sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );
}

